import React, { useEffect } from "react";
import ReactPaginate from "react-paginate";
import { useTranslation } from "react-i18next";

interface PaginationProps {
    perPage: number;
    totalRecords: number;
    onPageChange: (page: any) => void;
    onPageSizeChange?: (pageSize: number) => void;
    currentPage: number;
    pageSizeOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
    perPage,
    totalRecords,
    onPageChange,
    onPageSizeChange,
    currentPage,
    pageSizeOptions = [10, 25, 50, 100],
}) => {
    const { t } = useTranslation("common");
    const [showing, setShowing] = React.useState<string>("");
    useEffect(() => {
        let pageCount = Math.ceil(totalRecords / perPage);
        if (pageCount == 1) {
            setShowing(totalRecords.toString());
        }

        if (currentPage == pageCount) {
            setShowing(`${(currentPage - 1) * perPage + 1} - ${totalRecords}`);
        } else {
            setShowing(`${(currentPage - 1) * perPage + 1} - ${currentPage * perPage}`);
        }

        if (currentPage > 1 && Math.ceil(totalRecords / perPage) < currentPage) {
            onPageChange(currentPage - 1);
        }
    }, [totalRecords, perPage, currentPage]);

    return (
        <div className="flex items-center justify-between w-full mt-3">
            <div className="flex items-center gap-3">
                <div className="italic text-[0.8rem] text-[#656565]">
                    {totalRecords > 0 && t("pagination.showingRecords", { showing, total: totalRecords })}
                </div>
                {onPageSizeChange && (
                    <div className="flex items-center gap-2">
                        <span className="text-[0.8rem] text-[#656565]">{t("pagination.rowsPerPage")}</span>
                        <select
                            value={perPage}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="text-[0.8rem] border border-gray-300 rounded px-2 py-1 pr-8 bg-white cursor-pointer appearance-none"
                            style={{
                                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none'%3e%3cpath d='M7 7l3 3 3-3' stroke='%23d1d5db' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 0.5rem center',
                                backgroundSize: '1.5em 1.5em'
                            }}
                        >
                            {pageSizeOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                            <option key="all" value={999999}>
                                {t("pagination.all")}
                            </option>
                        </select>
                    </div>
                )}
            </div>
            {Math.ceil(totalRecords / perPage) > 1 && (
                <ReactPaginate
                    key={`pagination-${perPage}-${currentPage}`}
                    initialPage={currentPage - 1}
                    className="pagination"
                    breakLabel="..."
                    nextLabel=">"
                    onPageChange={(data: any) => {
                        onPageChange(data.selected + 1);
                    }}
                    pageRangeDisplayed={5}
                    pageCount={Math.ceil(totalRecords / perPage)}
                    previousLabel="<"
                    renderOnZeroPageCount={null}
                />
            )}
        </div>
    );
};

export default Pagination;
